import type { MockDatabase, MockRecord } from './database.ts';

/** Vendor signing approves the agreement (5), but the log records the signing action. */
export const VQCA_SIGN_NOTES: Readonly<Record<number, string>> = {
  2: 'SIGNEDBYGBLQLTYMGR',
  3: 'SIGNEDBYCATMGR',
  5: 'SIGNEDBYVENDOR',
};

/** Repair blank notes from action history and remove company names from internal signatures. */
export function migrateVqcaSignNotes(data: MockDatabase): boolean {
  const logsByAgreement = new Map<string, MockRecord[]>();
  for (const log of data.signLogs ?? []) {
    // Seeded/imported rows do not have the runtime's signer and creation metadata.
    if (!log.signedBy || !log.createdDate) continue;
    const id = String(log.vqcaId);
    const logs = logsByAgreement.get(id) ?? [];
    logs.push(log);
    logsByAgreement.set(id, logs);
  }
  const actionsByAgreement = new Map<string, { status: number; signedDate: unknown }[]>();
  for (const audit of data.audit ?? []) {
    if (audit.table !== 'vqcas' || !Array.isArray(audit.changes)) continue;
    const changes = audit.changes as Record<string, unknown>[];
    const transition = changes.find(change => change.field === 'statusId');
    const status = Number(transition?.newValue ?? transition?.new);
    const previous = Number(transition?.oldValue ?? transition?.old);
    const allowedPrevious: Record<number, number> = { 2: 1, 3: 2, 5: 3, 6: 1, 7: 2, 8: 5 };
    if (!transition || allowedPrevious[status] !== previous) continue;
    const signedDate = changes.find(change => change.field === 'signedDate');
    const id = String(audit.recordId);
    const actions = actionsByAgreement.get(id) ?? [];
    actions.push({ status, signedDate: signedDate?.newValue ?? signedDate?.new ?? audit.updatedDate });
    actionsByAgreement.set(id, actions);
  }
  let migrated = false;
  for (const [id, logs] of logsByAgreement) {
    const actions = actionsByAgreement.get(id);
    // Both tables are append-only in the signing handler. Do not guess if history is incomplete.
    if (!actions || actions.length !== logs.length) continue;
    logs.forEach((log, index) => {
      const action = actions[index];
      const note = VQCA_SIGN_NOTES[action.status];
      const elapsed = Date.parse(String(log.signedDate)) - Date.parse(String(action.signedDate));
      if (note && !String(log.notes ?? '').trim() && elapsed >= 0 && elapsed <= 1000) {
        log.notes = note;
        migrated = true;
      }
    });
  }
  for (const log of data.signLogs ?? []) {
    // Use the recorded signing action, not the account's current roles or company name.
    if (log.company && (log.notes === VQCA_SIGN_NOTES[2] || log.notes === VQCA_SIGN_NOTES[3])) {
      log.company = '';
      migrated = true;
    }
  }
  return migrated;
}